<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import Select, { type SelectChangeEvent } from 'primevue/select';
import ProgressSpinner from 'primevue/progressspinner';
import Badge from 'primevue/badge';
import { useI18n } from 'vue-i18n';
import { VList } from 'virtua/vue';
import {
  ButtonType,
  CustomButton,
  FormDiscardChangesAlert,
  EntryBaseItem,
  FieldSearch,
  EntryWelcome,
  getFieldValue
} from '@/components';
import { formattedDate, DATE_FORMAT_2, routeConstants, LocalStorageUtils, DATA_VIEW_FILTER } from '@/utils';
import { BACKEND_URL } from '@/services/apiClient';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import EntryNavigation from './EntryNavigation.vue';
import { useStudyStore } from '@/stores/study';
import { useEntryStore } from '@/stores/entry';
import { useFormStore } from '@/stores/forms';
import CaseNoteButton from '@/components/data/pipeline/CaseNoteButton.vue';
import EntryReasonDialog from './EntryReasonDialog.vue';
import {
  FormFieldType,
  type EntryUpdateReason,
  type FormField
} from '@/models';
import EntrySignHistoryDialog from './EntrySignHistoryDialog.vue';
import { useEntryListStore } from '@/stores/entryList.js';

const { t: $t } = useI18n();
const studyStore = useStudyStore();
const entryStore = useEntryStore();
const entryStoreList = useEntryListStore();
const formsStore = useFormStore();
const virtualizerRef = ref();
const route = useRoute();

const previewMode = ref(false);
const welcomeMode = ref(false);

const canShowCaseNotes = computed(() => {
  const flags = studyStore.selectedStudy?.flags as
    | Record<string, unknown>
    | undefined;
  return flags?.['enableCaseNotes'] === true;
});

const isNotesPinned = ref(false);
const handleNotesPinChange = (pinned: boolean) => {
  isNotesPinned.value = pinned;
};

const isAnonymousEntry = computed(() => {
  const publicStudyFormRegex = /^\/study\/[^/]+\/form\//;
  return publicStudyFormRegex.test(route.fullPath) || route.fullPath.startsWith('/form/');
});
const anonymousEntrySubmitted = ref(false);
const showReasonDialog = ref(false);
const showEntryHistoryDialog = ref(false);
const reasonType = ref<'update' | 'review'>('update');

const togglePreviewMode = () => {
  previewMode.value = !previewMode.value;
};

const onEditClick = () => {
  togglePreviewMode();
  welcomeMode.value = false;
};

const router = useRouter();
const onBackClick = async () => {
  reasonType.value = 'update';
  if (
    !welcomeMode.value &&
    entryStore.form?.welcomeScreenTemplate &&
    !route.params.entryIdentifier &&
    !entryStore.entry
  ) {
    welcomeMode.value = true;
    return;
  }
  if (!entryStore.isEntryPristine) {
    await entryStore.showDiscardChangesDialog();
  }
  if (
    !previewMode.value &&
    (route.params.entryIdentifier !== undefined || entryStore.entry)
  ) {
    togglePreviewMode();
    return;
  }
  if (
    studyStore.selectedStudy?.flags?.spRedirect &&
    !studyStore.studyPermissions?.manageInvest
  ) {
    handleSpDashboardRedirect();
    return;
  }
  //check if data filter, if it is entries then remove the selected case from the store
  if (LocalStorageUtils.getCurrentDataViewFilter(studyStore.selectedStudyId!) === DATA_VIEW_FILTER.ENTRIES) {
    entryStoreList.selectedCase = undefined;
  }
  router.back();
};

const onSaveClick = async (
  e?: PointerEvent,
  navigate?: boolean,
  reason?: EntryUpdateReason
) => {
  const selectedCase = entryStore.form?.selectedCase;
  const requiredUnansweredFields = entryStore.lookForRequiredFields();

  if (requiredUnansweredFields > 0) {
    const index = entryStore.form?.items?.findIndex(
      item => item.showRequiredError || item.invalidInput
    );
    if (index && index > -1) {
      const requiredField = entryStore.form?.items![index];
      const requiredFieldInsideSection = entryStore.form?.items!.find(
        item =>
          item.type === FormFieldType.SECTION &&
          item.options.fields?.includes(requiredField?.identifier ?? '')
      );
      if (requiredFieldInsideSection) {
        if (!requiredFieldInsideSection.sectionExpanded) {
          entryStore.expandSectionFields(requiredFieldInsideSection);
        }
      }
      nextTick(() => {
        virtualizerRef.value.scrollToIndex(index);
      });
    }
    return;
  }

  if (isAnonymousEntry.value && entryStore.form?.cachePublicEntry) {
    // cache the entry data locally.
    const entryToken = (route.query?.token as string) ?? undefined;
    const localPayload = {
      studyId: studyStore.selectedStudyId || entryStore.form?.studyId,
      items: entryStore.form.items,
      revisionDate: new Date().getTime(),
      entryToken,
      reason
    };

    localStorage.setItem(
      `publicEntry_${entryStore.form?.identifier}`,
      JSON.stringify(localPayload)
    );
  }

  if (studyStore.selectedStudy?.flags?.cfrPart11 && !reason) {
    // show reason dialog
    showReasonDialog.value = true;
    return;
  }

  if (entryStore.openDiscardChangesAlert) {
    entryStore.openDiscardChangesAlert = !entryStore.openDiscardChangesAlert;
  }
  await entryStore.submitEntry(navigate, reason);

  if (
    !route.params.entryIdentifier &&
    !navigate &&
    !isAnonymousEntry.value &&
    !route.query?.redirectUrl &&
    !(
      studyStore.selectedStudy?.flags?.spRedirect &&
      !studyStore.studyPermissions?.manageInvest
    )
  ) {
    router.replace({
      name: routeConstants.DATA.name,
      query: { caseFilter: selectedCase?.label }
    });
    return;
  }

  if (
    isAnonymousEntry.value ||
    route.query?.redirectUrl ||
    (studyStore.selectedStudy?.flags?.spRedirect &&
      !studyStore.studyPermissions?.manageInvest)
  ) {
    anonymousEntrySubmitted.value = true;
  }

  if (
    studyStore.selectedStudy?.flags?.spRedirect &&
    !studyStore.studyPermissions?.manageInvest
  ) {
    togglePreviewMode();
  }

  if (
    studyStore.selectedStudy?.flags?.spRedirect &&
    route.query?.autoRedirect != 'off'
  ) {
    setTimeout(() => {
      handleSpDashboardRedirect();
    }, 3000);
  }
};

const handleSpDashboardRedirect = () => {
  if (route.query?.redirectUrl) {
    window.open(`${route.query.redirectUrl}`, '_self');
  } else {
    window.open(
      `${import.meta.env.VITE_APP_STUDYPAGES_BASE_URL}/dashboard/`,
      '_self'
    );
  }
};

// language switching related
const { locale } = useI18n();

const onLanguageChange = (event: SelectChangeEvent) => {
  locale.value = event.value.languageCode;
  entryStore.switchLangauge(event);
};

const onFieldSearch = (fieldIndex: number) => {
  virtualizerRef.value.scrollToIndex(fieldIndex);
  entryStore.form!.items![fieldIndex].blink = true;
  setTimeout(() => {
    entryStore.form!.items![fieldIndex].blink = false;
  }, 1500);
};

const onCaseClick = () => {
  const selectedCase = entryStore.form?.selectedCase;
  router.replace({
    name: routeConstants.DATA.name,
    query: { caseFilter: selectedCase?.label }
  });
};

const loadForm = async () => {
  previewMode.value = route.params.entryIdentifier !== undefined;
  welcomeMode.value = false;
  if (formsStore.formList.length === 0) {
    formsStore.getStudyForms();
  }
  await entryStore.getForm();
  if (entryStore.form?.hasEntryPermission === false) {
    previewMode.value = true;
  }
  if (route.query?.token && entryStore.entry) {
    previewMode.value = entryStore.entryTokenPreview;
  } else if (entryStore.form?.welcomeScreenTemplate) {
    welcomeMode.value = true;
  }
};

onMounted(async () => {
  loadForm();
});

const backButtonVisible = computed(() => {
  if (isAnonymousEntry.value) {
    if (
      !previewMode.value &&
      !welcomeMode.value &&
      route.params.entryIdentifier
    ) {
      return true;
    }
    return false;
  }
  return true;
});

const currentParams = computed(() => ({
  formIdentifier: route.params.formIdentifier as string,
  entryIdentifier: route.params.entryIdentifier as string
}));

watch(
  currentParams,
  (newParams, oldParams) => {
    const formChanged = newParams.formIdentifier !== oldParams.formIdentifier;
    const entryChanged =
      newParams.entryIdentifier !== oldParams.entryIdentifier;

    if (formChanged || entryChanged) {
      loadForm();
    }
  },
  { immediate: false, deep: true }
);

const closeReasonDialog = () => {
  showReasonDialog.value = false;
};

const saveEntryWithReason = async (reason: EntryUpdateReason) => {
  showReasonDialog.value = false;
  reason.type = reasonType.value;
  onSaveClick(undefined, false, reason);
};

const onAddSignClick = () => {
  reasonType.value = 'review';
  showReasonDialog.value = true;
};

const showEntryHistory = () => {
  showEntryHistoryDialog.value = true;
};

const closeEntryHistoyDialog = () => {
  showEntryHistoryDialog.value = false;
};

const entryItems = computed(() => {
  return entryStore.form?.items?.filter(item => item.show !== false) || [];
});

const unansweredFields = computed(() => {
  return (
    entryStore.form?.items?.filter(
      item =>
        entryStore.entry?.completion?.unansweredFields?.includes(
          item.identifier
        ) &&
        item.show !== false &&
        (getFieldValue(item) === undefined ||
          getFieldValue(item) === '' ||
          getFieldValue(item) === null)
    ) || []
  );
});

const onIncompleteFieldClick = (field: FormField) => {
  const items =
    entryStore.form?.items?.filter(item => item.show !== false) || [];
  const index = items.findIndex(item => item.identifier === field.identifier);
  if (index !== undefined && index > -1) {
    const unansweredInsideSection = entryStore.form?.items!.find(
      item =>
        item.type === FormFieldType.SECTION &&
        item.options.fields?.includes(field.identifier)
    );
    if (unansweredInsideSection) {
      if (!unansweredInsideSection.sectionExpanded) {
        entryStore.expandSectionFields(unansweredInsideSection);
      }
    }
    nextTick(() => {
      virtualizerRef.value.scrollToIndex(index);
      items[index].blink = true;
      setTimeout(() => {
        items[index].blink = false;
      }, 1500);
    });
  }
};

const scrollToNextSectionField = (currentField: FormField) => {
  const items =
    entryStore.form?.items?.filter(item => item.show !== false) || [];

  const currentSectionField = items.find(
    item =>
      item.type === FormFieldType.SECTION &&
      item.identifier === currentField.sectionId
  );
  if (currentSectionField) {
    entryStore.expandSectionFields(currentSectionField);
  }

  const index = items.findIndex(
    item => item.identifier === currentField.identifier
  );
  if (index !== undefined && index > -1) {
    const nextSectionField = items.find(
      (item, i) => i > index && item.type === FormFieldType.SECTION
    );
    if (nextSectionField) {
      entryStore.expandSectionFields(nextSectionField);
      nextTick(() => {
        virtualizerRef.value.scrollToIndex(items.indexOf(nextSectionField));
      });
    }
  }
};
</script>

<template>
  <div :class="[
      'entry-root',
      {
        'entry-root-without-navigation':
          isAnonymousEntry || formsStore.formList.length < 2,
        'has-incomplete-fields': unansweredFields.length > 0,
        'notes-pinned': isNotesPinned
      },
    ]">
    <div class="entry-wrapper-root"
      v-if="entryStore.getFormLoader?.isLoading === false && !entryStore.formClosedError && !entryStore.anonymousResponsesNotAllowed">
      <div class="navigation-bar"
        v-if="
          !isAnonymousEntry &&
          formsStore.formList.length > 1 &&
          entryStore.form?.selectedCase
        ">
        <EntryNavigation />
      </div>
      <div class="entry-wrapper">
        <div class="header-root" v-if="entryStore.form">
          <div class="header">
            <div class="header-content">
              <div class="header-title">
                <div class="selected-case"
                  v-if="entryStore.form.selectedCase"
                  @click="onCaseClick"
                  v-ripple>
                  <FontAwesomeIcon :icon="[`fas`, `folder`]"
                    class="selected-case-icon"/>
                  <div class="selected-case-label">
                    {{ entryStore.form.selectedCase?.label }}
                  </div>
                  <FontAwesomeIcon :icon="['far', `chevron-right`]" />
                </div>
                <h1>{{ entryStore.form!.title }}</h1>
                <Badge :value="$t('preview_label')"
                  v-if="
                    previewMode &&
                    !entryStore.entryLocked &&
                    !entryStore.entry?.archivedAt
                  "
                  class="preview"
                  severity="contrast"/>
                <Badge :value="$t('locked_label')"
                  v-if="entryStore.entryLocked"
                  class="preview"
                  severity="contrast"/>
                <Badge :value="$t('archived_label')"
                  v-if="entryStore.entry?.archivedAt"
                  class="preview"
                  severity="contrast"/>
              </div>
              <div class="header-buttons">
                <CaseNoteButton :caseModel="entryStore.form.selectedCase"
                  class="header-note-button"
                  :showText="true"
                  @onPinChange="handleNotesPinChange"
                  v-if="entryStore.form.selectedCase && canShowCaseNotes"/>
                <Select v-model="entryStore.selectedLanguage"
                  placeholder="Language"
                  optionLabel="language"
                  @change="onLanguageChange"
                  v-if="entryStore.form.formTranslations"
                  :options="entryStore.form.formTranslations"
                  class="translation-input"/>
                <CustomButton :text="previewMode ? $t('back') : $t('cancel')"
                  @onClick="onBackClick"
                  :margin-left="16"
                  v-if="backButtonVisible"
                  :type="ButtonType.OUTLINED"/>
                <CustomButton :text="entryStore.form.entrySaveButtonText ?? $t('save')"
                  :margin-left="16"
                  v-if="
                    !previewMode &&
                    !welcomeMode &&
                    !entryStore.anonymousEntrySubmitError &&
                    entryStore.form?.hasEntryPermission !== false &&
                    entryStore.multipleEntriesError === undefined
                  "
                  @onClick="onSaveClick"
                  :type="ButtonType.POSITIVE"/>
                <CustomButton :text="$t('edit')"
                  :margin-left="16"
                  v-if="
                    previewMode &&
                    !entryStore.entryLocked &&
                    !anonymousEntrySubmitted &&
                    !entryStore.entry?.archivedAt &&
                    entryStore.form?.editExistingEntry
                  "
                  :disabled="entryStore.entryDisableEdit !== undefined"
                  :tooltip="entryStore.entryDisableEdit"
                  @onClick="onEditClick"
                  :type="ButtonType.POSITIVE"/>
                <CustomButton :text="$t('add_sign')"
                  :margin-left="16"
                  v-if="
                    previewMode &&
                    !entryStore.entryLocked &&
                    !anonymousEntrySubmitted &&
                    !entryStore.entry?.archivedAt &&
                    studyStore.selectedStudy?.flags?.cfrPart11
                  "
                  @onClick="onAddSignClick"
                  :type="ButtonType.OUTLINED"/>

                <CustomButton :text="$t('view_sign')"
                  :margin-left="16"
                  v-if="
                    previewMode &&
                    !entryStore.entryLocked &&
                    !anonymousEntrySubmitted &&
                    !entryStore.entry?.archivedAt &&
                    studyStore.selectedStudy?.flags?.cfrPart11
                  "
                  @onClick="showEntryHistory"
                  :type="ButtonType.OUTLINED"/>
              </div>
            </div>
            <div class="header-edited-by" v-if="entryStore.entry">
              {{ $t("edited_on") }}
              {{ formattedDate(entryStore.entry.updatedAt, DATE_FORMAT_2) }}
              {{ entryStore.entry.creatorNames ? $t("by") : "" }}
              <img :src="`${BACKEND_URL}${entryStore.entry.creatorAvatar}`"
                class="avatar"
                v-if="entryStore.entry.creatorAvatar"/>
              {{
                entryStore.entry.creatorNames
                  ? entryStore.entry.creatorNames
                  : ""
              }}
            </div>
          </div>
          <FieldSearch v-if="
              (!welcomeMode || previewMode) &&
              !anonymousEntrySubmitted &&
              studyStore.selectedStudy?.flags?.customDataDictionary === 'PUMP'
            "
            :fields="entryStore.form ? entryStore.form!.items! : []"
            @onFieldSelected="onFieldSearch"
            :previewMode="previewMode"
            :newEntry="!route.params.entryIdentifier"/>
        </div>

        <div class="submitted-message" v-if="anonymousEntrySubmitted">
          <h3 v-if="entryStore.anonymousEntrySubmitError" class="submitted-error-message">
            <FontAwesomeIcon :icon="['far', 'circle-exclamation']" class="icon" />
            {{
              entryStore.anonymousEntrySubmitError
            }}
          </h3>
          <h3 v-else>
            {{ $t("form_thanks_message_after_submission") }}
          </h3>
          <div class="redirect-back"
            v-if="
              route.query?.autoRedirect != 'off' &&
              studyStore.selectedStudy?.flags?.spRedirect
            ">
            <div>{{ $t("back_sp") }}</div>
            <ProgressSpinner strokeWidth="2" class="progress" />
          </div>
        </div>
        <div class="restored-data-root" v-if="entryStore.restoredEntryData">
          <div class="restored-data">
            <div class="restored-data-text">
              {{ $t("entry_data_restored") }}
              <CustomButton text="Reset"
                @onClick="entryStore.resetRestoredEntryData()"
                :margin-left="16"
                :type="ButtonType.OUTLINED"/>
            </div>
            
            <FontAwesomeIcon :icon="['fas', 'close']"
              @click="entryStore.hideRestoredEntryDataMsg()"
              class="close-restored-data"
              aria-label="Close restored data message"/>
          </div>
        </div>
        <template v-if="welcomeMode && !previewMode && entryStore.multipleEntriesError === undefined">
          <EntryWelcome @update:welcomeMode="welcomeMode = false"
            :template="entryStore.form?.welcomeScreenTemplate || ''"/>
        </template>
        <template v-else-if="entryStore.form && entryStore.multipleEntriesError === undefined">
          <VList :data="entryItems"
            #default="{ item }"
            ref="virtualizerRef"
            class="v-list">
            <EntryBaseItem :field="item"
              :previewMode="previewMode"
              :selectedCase="entryStore.form?.selectedCase"
              :key="item.identifier"
              @toNextSection="scrollToNextSectionField"
              @onSaveEntry="onSaveClick"
              v-show="
                item.show !== false &&
                item.expanded == true &&
                !item.archivedAt
              "/>
          </VList>
        </template>

        <template v-else-if="entryStore.multipleEntriesError">
          <div class="submitted-message submitted-error-message">
            <h3>{{ entryStore.multipleEntriesError }}</h3>
          </div>
        </template>
      </div>
    </div>

    <div class="entry-closed-error" v-if="entryStore.formClosedError">
      <div class="entry-closed-message">
        <FontAwesomeIcon :icon="['far', 'file-lock']" class="closed-icon" />
        <p>{{ entryStore.formClosedMessage || $t('form_closed_message') }}</p>
      </div>
    </div>

    <div class="entry-closed-error" v-if="entryStore.anonymousResponsesNotAllowed">
      <div class="entry-closed-message">
        <p class="closed-title">{{ $t('form_no_longer_available_title') }}</p>
        <p class="closed-subtitle">{{ $t('form_no_longer_available_subtitle') }}</p>
      </div>
    </div>

    <div class="entry-loader" v-if="entryStore.getFormLoader.isLoading">
      <ProgressSpinner strokeWidth="4" />
    </div>
  </div>
  <div class="incomplete-fields-container"
    v-if="unansweredFields.length > 0">
    <h3>Incomplete Fields</h3>
    <ul>
      <li v-for="(field, index) in unansweredFields"
        :key="index"
        class="incomplete-field"
        @click="onIncompleteFieldClick(field)"
        tabindex="0">
        <FontAwesomeIcon :icon="['fas', 'exclamation-triangle']"
          class="incomplete-icon"/>
        <span class="incomplete-label">{{ field.label }}</span>
      </li>
    </ul>
  </div>

  <FormDiscardChangesAlert :visible="entryStore.openDiscardChangesAlert"
    v-if="entryStore.openDiscardChangesAlert"
    @onSave="onSaveClick(undefined, true)"
    @onDiscard="entryStore.discardEntryChanges"
    @onClose="entryStore.cancelDiscardChangesDialog"/>

  <EntryReasonDialog :visible="showReasonDialog"
    @onClose="closeReasonDialog"
    @onSave="saveEntryWithReason"/>

  <EntrySignHistoryDialog :visible="showEntryHistoryDialog"
    @onClose="closeEntryHistoyDialog"/>
</template>

<style scoped lang="scss">
@import "../../../assets/variables.module";
.entry-root {
  width: 100%;
  max-width: 1080px;
  padding: 12px 0px 20px 80px;
  transition: padding-right 0.2s ease;

  // Dynamic right padding: only add what's needed to push entry content clear of the
  // pinned notes panel (428px). The sidebar occupies 260px on the left, so the entry
  // fills up to 260 + 1080 = 1340px. The notes panel starts at (100vw - 428px).
  // Overlap = 1768px - 100vw; add 16px gap → padding = calc(1784px - 100vw).
  // Above 1768px the two regions no longer overlap so no padding is needed.
  &.notes-pinned {
    @media (max-width: 1767px) {
      padding-right: min(calc(1784px - 100vw), calc(428px + 16px));
    }
  }
}
.entry-root-without-navigation {
  padding: 48px 48px 20px;
}
.entry-wrapper-root {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
}
.navigation-bar {
  display: flex;
  width: 100%;
  max-width: 1000px;
  position: relative;
  background-color: white;
}
.has-incomplete-fields {
  padding-right: 0px;
}
.entry-wrapper {
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: white;
  box-shadow: 0 0 8px 2px rgba(0, 0, 0, 0.15);
  position: relative;
  overflow: auto;
  flex: 1;
}
.header-root {
  position: sticky;
  z-index: 6;
  padding: 12px 80px;
  display: flex;
  justify-content: center;
  border-bottom: 1px solid #ced3d9;
  background-color: #f5f7f7;
  width: 100%;
}
.header {
  width: 100%;
}
.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.header-title {
  flex: 1;
  display: flex;
  align-items: center;
  padding-right: 16px;

  h1 {
    font-weight: 700;
    font-size: 20px;
    box-shadow: none;
  }

  .preview {
    margin-left: 8px;
    text-wrap: nowrap;
  }
  .p-badge {
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 2px;
    padding: 0 12px;
    background-color: #354a4c;
    border-radius: 4px;
  }
}
.header-buttons {
  display: flex;
}

.header-note-button {
  margin-right: 4px;
}

.v-list {
  height: 100%;
  flex: 1;
  padding: 24px 80px 12px 80px;
  position: relative;
}
.restored-data-root {
  padding: 24px 80px 0px 80px;
}
.restored-data {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #000;
  padding: 12px 20px;
  border-radius: 8px;

  .close-restored-data {
    font-size: 20px;
    margin-left: 16px;
    cursor: pointer;
  }

  .restored-data-text {
    display: flex;
    align-items: center;
  }
}
.entry-closed-error {
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.entry-closed-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  max-width: 480px;
  text-align: center;
  padding: 40px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 0 8px 2px rgba(0, 0, 0, 0.15);

  .closed-icon {
    font-size: 48px;
    color: #6c757d;
  }

  p {
    margin: 0;
    color: #354a4c;
    line-height: 1.6;
    font-size: 18px;
  }

  .closed-title {
    font-size: 18px;
    font-weight: 600;
  }

  .closed-subtitle {
    font-size: 16px;
    font-weight: 400;
  }
}
.entry-loader {
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.header-edited-by {
  display: flex;
  align-items: center;
  margin-top: 8px;
  color: #575e66;
}
.avatar {
  width: 24px;
  margin-left: 8px;
  margin-right: 8px;
  border-radius: 4px;
}
.submitted-message {
  padding: 24px 80px 12px 80px;
  text-align: center;

  h3 {
    font-weight: 700;
  }
}
.submitted-error-message {
  color: #ea3546;
  font-weight: 700;
}
.redirect-back {
  margin-top: 16px;
  display: flex;
  justify-content: center;

  .progress {
    width: 24px;
    height: 24px;
    margin: unset;
    margin-left: 16px;
  }
}
.selected-case {
  display: flex;
  align-items: center;
  margin-right: 8px;

  &:hover {
    cursor: pointer;
  }

  .selected-case-icon {
    color: $primary-color;
    font-size: 20px;
    margin-right: 8px;
  }

  .selected-case-label {
    font-weight: 600;
    margin-right: 8px;
  }
}

.incomplete-fields-container {
  padding: 48px 32px;
  background: linear-gradient(90deg, #f8fafc 0%, #eaf3fa 100%);
  border-radius: 12px;
  box-shadow: 0 2px 12px 0 rgba(60, 80, 120, 0.07);
  max-width: 600px;
  overflow: auto;
  h3 {
    font-size: 1.2rem;
    font-weight: 700;
    margin-bottom: 16px;
    color: #2a3a4b;
    letter-spacing: 1px;
  }
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  .incomplete-field {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 16px;
    margin-bottom: 8px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 4px 0 rgba(60, 80, 120, 0.04);
    font-size: 1rem;
    color: #2a3a4b;
    cursor: pointer;
    transition: background 0.2s, box-shadow 0.2s;
    outline: none;
    border: none;
    &:hover,
    &:focus {
      background: #eaf3fa;
      box-shadow: 0 2px 8px 0 rgba(60, 80, 120, 0.1);
    }
    .incomplete-icon {
      color: #e67e22;
      font-size: 1.1em;
    }
    .incomplete-label {
      flex: 1;
      font-weight: 500;
      letter-spacing: 0.5px;
      word-break: break-word;
    }
  }
}

@media (max-width: 1440px) {
  .entry {
    &-root {
      height: 100%;
      width: 100%;
      padding: 12px 48px 20px;
    }
    &-body {
      padding: 24px 40px 12px;
    }
  }
  .entry-root-without-navigation {
    padding: 48px 48px 20px;
  }
  .header-root {
    padding: 12px 40px;
  }
  .navigation-bar {
    width: 100%;
  }
  .v-list {
    padding: 12px 40px;
  }
}
@media (max-width: 600px) {
  .entry {
    &-root {
      padding: 12px 0 0;
    }
    &-body {
      padding: 54px 15px 12px;
    }
  }
  .header {
    &-root {
      padding: 12px 15px;
    }
  }
  .root {
    padding: 0px 16px !important;
  }
  .entry-anonymous-views {
    .branding {
      margin: 0;
      padding: 40px 0 30px;
    }
  }
  :deep(.p-datatable-tbody > tr > td) {
    padding: 12px;
  }
  :deep(.p-datatable-header-cell) {
    padding: 3px;
  }
  :deep(.row-header) {
    min-width: fit-content !important;
  }
  :deep(.p-datatable-frozen-column) {
    padding: 3px 6px !important;
  }
  :deep(.p-fileupload-header) {
    padding: 12px;
  }
  :deep(.p-fileupload-content) {
    padding: 0 12px 12px;
  }
  .v-list {
    padding: 0px;
  }
  .navigation-bar {
    padding: 0 12px;
  }
  .restored-data-root {
    padding: 12px;
  }
}
</style>
